import React, { FC, useEffect, useState } from 'react';
import { AutocompleteItem, ChipsInput } from '@prismamedia/one-components';
import { isDefined } from '../../../utils/isDefined';
import {
  useAuthorsGetter,
  useAuthorGetter,
} from '../../../apollo/queries/authors.web.graphql';
import {
  AuthorOrderingInput,
  BrandKey,
} from '../../../__generated__/queries-web';

interface AuthorsSelectProps {
  label: string;
  brandKey?: BrandKey;
  className?: string;
  value?: string[];
  onChange?: (value?: string[]) => void;
}

export const AuthorsSelect: FC<AuthorsSelectProps> = ({
  label,
  brandKey,
  className,
  value,
  onChange,
}) => {
  const [internalValue, setInternalValue] = useState<AutocompleteItem[]>([]);
  const getAuthors = useAuthorsGetter();
  const getAuthor = useAuthorGetter();

  const fetchList = (search: string) =>
    getAuthors({
      first: 100,
      where: {
        ...(search && { name_contains: search }),
        ...(brandKey && { brandKey }),
      },
      orderBy: [AuthorOrderingInput.name_ASC],
    }).then((data) =>
      data?.authors
        .filter(({ name }) => !!name)
        .map(({ id, name }) => ({ id, label: name })),
    );

  useEffect(() => {
    if (!value) {
      setInternalValue([]);
      return;
    }
    Promise.all(value.map((id) => getAuthor({ where: { id } }))).then((res) =>
      setInternalValue(
        res
          .map((data) => data.author)
          .filter(isDefined)
          .map((author) => ({
            id: author.id,
            label: author.name,
          })),
      ),
    );
  }, [value, getAuthor]);

  return (
    <ChipsInput
      variant="outlined"
      label={label}
      value={internalValue}
      className={className}
      onChange={(val) =>
        onChange?.(val?.length ? val.map(({ id }) => id) : undefined)
      }
      autocomplete={{ fetchList }}
    />
  );
};
